import { motion } from 'framer-motion';
import { Clapperboard, BadgeCheck, Fingerprint, Users } from 'lucide-react';
import { useSiteConfig } from '../../hooks/useSiteConfig';

const iconMap: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  Clapperboard,
  BadgeCheck,
  Fingerprint,
  Users,
};

export default function ServicesSection() {
  const siteConfig = useSiteConfig();
  const { services } = siteConfig;

  return (
    <section id="services" className="py-20 md:py-28 bg-deep-black relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {services.sectionTitle}
          </h2>
          <p className="text-lg text-neon-purple-glow mb-3">{services.subtitle}</p>
          <p className="text-sm text-gray-500 max-w-2xl mx-auto">{services.intro}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {services.services.map((service, index) => {
            const Icon = iconMap[service.icon] || Clapperboard;
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="neon-card rounded-2xl p-7 hover:-translate-y-1 transition-all duration-300 group relative overflow-hidden"
              >
                <div className="absolute -top-8 -right-8 w-24 h-24 bg-neon-cyan/10 rounded-full blur-2xl group-hover:bg-neon-cyan/20 transition-colors" />
                <div className="relative z-10">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-neon-cyan/20 to-neon-purple/20 flex items-center justify-center border border-neon-cyan/30 group-hover:border-neon-cyan/60 transition-colors">
                      <Icon size={26} className="text-neon-cyan" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-1">
                        {service.title}
                      </h3>
                      <p className="text-xs text-neon-gold font-medium">{service.tagline}</p>
                    </div>
                  </div>
                  {service.image && (
                    <div className="mb-4 rounded-xl overflow-hidden border border-neon-cyan/10 bg-deep-navy/50">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full aspect-[4/3] object-contain group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <p className="text-gray-400 text-sm leading-relaxed mb-4">{service.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {service.tags.map((tag, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 rounded-full text-xs font-medium bg-white/5 border border-neon-cyan/20 text-neon-cyan-glow"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
